'use strict'

// noinspection JSClosureCompilerSyntax
/**
 * @class ExternalCommentary
 * @extends {HTMLElement}
 * @author Jared Cowan <jcowan@redventures.com>
 * @description A web component for displaying external commentary.
 * @jira_ticket BRC-1004
 */
class ExternalCommentary extends HTMLElement {
  constructor() {
    super()

    // Create the shadow root for the component
    const shadowRoot = this.attachShadow({ mode: 'open' })

    // Attributes
    this.question = null
    this.experts = {}
    this.answers = {}

    // Create the component's DOM variables
    this.selectedAnswer = null
    this.selectedExpert = null

    // Markup template
    shadowRoot.innerHTML = `
      <link rel="preload" href="https://www.bankrate.com/static/fonts/CircularStd-Book.woff2" as="font" crossorigin />
      <link rel="preload" href="https://www.bankrate.com/static/fonts/CircularStd-Bold.woff2" as="font" crossorigin />
      <link href="${
        window.mixCdn === '/' ? '' : window.mixCdn
      }/css/brcom/external-commentary.css" rel="stylesheet" type="text/css">
      <section class="external-commentary--webcomponent">
          <h2 class="ec-title-wrapper">
            <span class="ec-title--preface">Ask the experts:&nbsp;</span>
            <span class="ec-title"></span>
          </h2>

          <dev class="ec-experts--row">
            <fieldset id="experts">
              <div class="ec-expert --selected ec-expert--1">
                <label for="expert1" class="ec-expert-label">
                  <input type="radio" id="expert1" name="experts" value="1" checked/>
                  <div class="ec-expert-image">
                    <img src="" alt="image of expert">
                  </div>
                  <div class="ec-expert-content">
                    <div class="ec-expert-name"></div>
                  </div>
                </label>
              </div>
              <div class="ec-expert ec-expert--2">
                <label for="expert2" class="ec-expert-label">
                  <input type="radio" id="expert2" name="experts" value="2"/>
                  <div class="ec-expert-image">
                    <img src="" alt="image of expert">
                  </div>
                  <div class="ec-expert-content">
                    <div class="ec-expert-name"></div>
                  </div>
                </label>
              </div>
              <div class="ec-expert ec-expert--3">
                <label for="expert3" class="ec-expert-label">
                  <input type="radio" id="expert3" name="experts" value="3"/>
                  <div class="ec-expert-image">
                    <img src="" alt="expert">
                  </div>
                  <div class="ec-expert-content">
                    <div class="ec-expert-name"></div>
                  </div>
                </label>
              </div>
            </fieldset>
          </dev>
          
          <div class="ec-answer-wrapper">
            <div class="ec-selected-expert-wrapper">
                <div class="ec-selected-expert-image">
                  <img src="" alt="image of expert">
                </div>
                <div class="ec-selected-expert-content">
                  <a class="ec-selected-expert-name" href="">
                    <h3 class="ec-selected-expert-name--header"></h3>
                  </a>
                  <div class="ec-selected-expert-title"></div>
                </div>
            </div>
            <article class="ec-answer"></article>
          </div>
      </section>
    `

    // Elements
    this.questionTitle = shadowRoot.querySelector('.ec-title')
    this.expertsWrapper = shadowRoot.querySelector('.ec-experts--row')
    this.expertsElm = shadowRoot.querySelectorAll('.ec-experts')
    this.expertsFieldset = shadowRoot.querySelector('#experts')
    this.expert1 = shadowRoot.querySelector('.ec-expert--1')
    this.expert2 = shadowRoot.querySelector('.ec-expert--2')
    this.expert3 = shadowRoot.querySelector('.ec-expert--3')
    this.answerbox = shadowRoot.querySelector('.ec-answer')
    this.selectedExpertImage = shadowRoot.querySelector(
      '.ec-selected-expert-image img'
    )
    this.selectedExpertName = shadowRoot.querySelector(
      '.ec-selected-expert-name'
    )
    this.selectedExpertNameHeader = shadowRoot.querySelector(
      '.ec-selected-expert-name--header'
    )
    this.selectedExpertTitle = shadowRoot.querySelector(
      '.ec-selected-expert-title'
    )

    // Event listener for the experts radio buttons
    this.expertsFieldset.addEventListener(
      'change',
      this.toggleChangeExpertCB.bind(this)
    )
  }

  /**
   * @static
   * @description sets the name of the element
   * @returns {string}
   */
  static get elementName() {
    return 'external-commentary'
  }

  /**
   * @static
   * @native Native component method
   * @uses attributeChangedCallback
   * @description Collection of attributes that can be observed
   * @returns {Array}
   */
  static get observedAttributes() {
    return ['question', 'experts']
  }

  /**
   * @callback Native component Callback
   * @description Calls when the component is mounted in dom
   * @returns {void}
   */
  connectedCallback() {
    this.setExpert(1)
  }

  /**
   * @callback Native component Callback
   * @description Calls when attributes in `observedAttributes` are changed
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   * @returns {void}
   */
  attributeChangedCallback(name, oldValue, newValue) {
    switch (name) {
      case 'question':
        this.questionTitle.textContent = newValue || null
        break
      case 'experts':
        Array.from(JSON.parse(newValue)).forEach((expert, n) => {
          this.experts[n + 1] = expert
          this.answers[n + 1] = expert.answer

          this.shadowRoot.querySelector(
            `.ec-expert--${n + 1} .ec-expert-image img`
          ).src =
            expert.avatar_url +
            '?auto=webp&fit=crop&quality=100&height=70&width=70'
          this.shadowRoot.querySelector(
            `.ec-expert--${n + 1} .ec-expert-name`
          ).textContent = expert.name
        })
        break
    }
  }

  /**
   * @param {Event} event
   * @uses setExpert
   */
  toggleChangeExpertCB(event) {
    this.setExpert.call(this, Number(event.target.value))
  }

  /**
   *
   */
  setExpert(indexOfAuthor = 1) {
    if (!Number.isInteger(indexOfAuthor)) {
      throw new Error('indexOfAuthor must be a number')
    }
    let expert = this.experts[indexOfAuthor]
    let answer = this.answers[indexOfAuthor]

    this.selectedExpert = expert
    this.selectedAnswer = answer

    // Set content
    this.answerbox.textContent = this.selectedAnswer
    this.selectedExpertNameHeader.textContent = this.selectedExpert.name
    this.selectedExpertName.href = this.selectedExpert.author_page
    this.selectedExpertTitle.textContent = this.selectedExpert.job_title
    this.selectedExpertImage.src =
      this.selectedExpert.avatar_url +
      '?auto=webp&fit=crop&quality=100&height=70&width=70'

    let isMobile = window.matchMedia('(max-width: 700px)').matches
    let elmLeft =
      this[`expert${indexOfAuthor}`].offsetLeft +
      this[`expert${indexOfAuthor}`].offsetWidth
    let elmParentLeft =
      this.expertsFieldset.offsetLeft + this.expertsFieldset.offsetWidth

    this.shadowRoot
      .querySelector('.ec-expert.--selected')
      .classList.toggle('--selected')

    setTimeout(
      () => {
        this.shadowRoot
          .querySelector(`.ec-expert--${indexOfAuthor}`)
          .classList.toggle('--selected')
      },
      isMobile ? 210 : 0
    )

    if (isMobile) {
      let offset = 0
      if (indexOfAuthor !== this.answers.length) {
        offset = 40
      }

      if (elmLeft >= elmParentLeft + this.expertsFieldset.scrollLeft) {
        this.expertsFieldset.scrollBy({
          left: this[`expert${indexOfAuthor}`].offsetLeft - offset,
          behavior: 'smooth'
        })
      } else if (elmLeft <= elmParentLeft + this.expertsFieldset.scrollLeft) {
        this.expertsFieldset.scrollBy({
          left: -this[`expert${indexOfAuthor}`].offsetWidth,
          behavior: 'smooth'
        })
      }
    }
  }
} // End of class WebComponentAgreeButton

window.customElements.define(ExternalCommentary.elementName, ExternalCommentary)
